import classes from "../homePageNew.module.css";
import { RetryIcon } from "./icons";
import { pillClass, statusLabel, auditTypeLabel, retryTransaction, firstOf, resolveMetrics, mapLlmLabels } from "../api/homePageApi";
import BrandAvatar from "../../../Component/Avatar/Avatar";
import MiniScoreRing from "./MiniScoreRing";
import OverflowPill from "./OverflowPill";

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function RecordCard({ record, selected, onSelect, onRetried, variant = "brand" }) {
  // Merged Brand Assessment tab (V2): the variant is per record, not per tab
  const isCategoryRecord = record.recordKind === "categoryAnalysis";
  const isCategoryVariant = variant === "category" || isCategoryRecord;
  const status = pillClass(record.status);
  const isFailed = status === "failed";
  const label = isCategoryVariant ? firstOf(record.category) || "Untitled" : record.brand || firstOf(record.category) || "Untitled";
  const category = firstOf(record.category);
  // Category Analysis has no main brand — the record's own scores/ring don't
  // apply (a category run holds a whole competitor leaderboard, not one
  // brand's score), so this is only read for the brand-variant card.
  const { scores } = resolveMetrics(record);
  // Category Analysis's "subtitle" line is competitor coverage, not a
  // repeat of the category name (which is already the heading here).
  const competitorCount = isCategoryVariant ? (record.competitor?.[category]?.length ?? 0) : 0;
  // Test vs Final — only Brand Audit/Category Analysis records carry this
  // field, so the chip is purely data-driven rather than variant-gated.
  const auditType = record.auditType ? auditTypeLabel(record.auditType) : null;

  const cardClassName = [classes.mdCard, selected ? classes.mdCardSelected : "", isFailed ? classes.mdCardFailed : ""]
    .filter(Boolean)
    .join(" ");

  const handleRetryClick = async (e) => {
    e.stopPropagation();
    await retryTransaction(record.transactionId);
    onRetried?.();
  };

  return (
    <div className={cardClassName} onClick={() => onSelect(record)}>
      <div className={classes.cardTop}>
        {/* Category Analysis has no brand/domain to show a logo for — the
            category name is already the heading, an avatar would just be a
            generic placeholder circle. */}
        {!isCategoryVariant && <BrandAvatar domainName={record.brandDomain} brandName={label} className={classes.av} />}
        <div className={classes.cardTxt}>
          <div className={classes.nameRow}>
            {isCategoryVariant ? (
              <OverflowPill values={record.category} label="Categories" className={classes.cardCategoryAsName} />
            ) : (
              <span className={classes.name} title={label}>
                {label}
              </span>
            )}
            {/* Category-variant cards hide the chip in the Category Analysis tab (V1); in the merged tab it reads "Category". */}
            {record.type && (!isCategoryVariant || isCategoryRecord) && <span className={classes.typeTag}>{record.type}</span>}
            {auditType && (
              <span className={`${classes.auditTypeChip} ${auditType === "Test" ? classes.auditTypeChipTest : classes.auditTypeChipFinal}`}>
                {auditType}
              </span>
            )}
          </div>
          {isCategoryVariant
            ? competitorCount > 0 && (
                <div className={classes.cardCategory}>
                  {competitorCount} competitor{competitorCount !== 1 ? "s" : ""} tracked
                </div>
              )
            : category && (
                // OverflowPill (not plain text) so a record with several
                // categories shows "First +N" with every category on hover.
                <div className={`${classes.cardCategory} ${classes.cardCategoryWrap}`}>
                  <OverflowPill values={record.category} label="Categories" className={classes.cardCategoryText} />
                </div>
              )}
        </div>
        <div className={classes.cardTopRight}>
          {isFailed ? (
            <>
              <span className={`${classes.pill} ${classes.pillFailed}`}>{statusLabel(record.status)}</span>
              <div className={classes.retryIconBtn} onClick={handleRetryClick} title="Retry this run">
                <RetryIcon size={13} />
              </div>
            </>
          ) : (
            <>
              {!isCategoryVariant && <MiniScoreRing score={scores?.brand_index_score} />}
              <span className={`${classes.pill} ${classes[`pill${status.charAt(0).toUpperCase()}${status.slice(1)}`]}`}>
                {statusLabel(record.status)}
              </span>
            </>
          )}
        </div>
      </div>
      <div className={classes.cardBottom}>
        <div className={classes.cardPills}>
          <OverflowPill values={record.market} label="Markets" />
          <OverflowPill values={record.persona} label="Personas" />
          <OverflowPill values={mapLlmLabels(record.llm)} label="Models" mono />
        </div>
        <span className={classes.cardDate}>{formatDate(record.runDate)}</span>
      </div>
    </div>
  );
}
